import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { Card, ErrorBox, Skeleton } from "../../components/ui";
import { usePeopleOverview } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { heat } from "./shared";

/** Competency heat matrix, skill by company (WP-23.1). */
export default function Matrix({ company }: { company?: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const ov = usePeopleOverview(company);
  const data = ov.data;

  if (ov.isError) return <div className="mt-6"><ErrorBox error={ov.error} onRetry={() => void ov.refetch()} /></div>;
  if (ov.isLoading || !data) return <div className="mt-6 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;
  const matrixCompanies = [...new Set((data.matrix ?? []).map((m) => m.company).filter(Boolean))].sort() as string[];
  const cell = (skill: string, c: string) => data.matrix.find((m) => m.skill === skill && m.company === c);

  return (
    <div data-ld-matrix>
      {/* competency matrix */}
      <Card className="mt-6 overflow-hidden">
        <div className="flex items-center justify-between border-b border-brand-grey-light px-4 py-3">
          <h2 className="font-heading text-base font-semibold">{t("ppl.matrix")}</h2>
          <span className="text-xs text-brand-grey">{t("ppl.matrixHint")}</span>
        </div>
        {data.skills.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("ppl.noSkills")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
                <tr>
                  <th className="px-4 py-2">{t("ppl.skill")}</th>
                  <th className="px-4 py-2 text-right">{t("ppl.holders")}</th>
                  <th className="px-4 py-2 text-right">{t("ppl.proficient")}</th>
                  {matrixCompanies.map((c) => <th key={c} className="px-3 py-2 text-center"><CompanyLogo company={c} size={14} /></th>)}
                </tr>
              </thead>
              <tbody>
                {data.skills.map((s) => (
                  <tr key={s.skill} className="border-t border-brand-grey-light">
                    <td className="px-4 py-2 font-semibold">{s.skill}</td>
                    <td className="px-4 py-2 text-right">{s.employees}</td>
                    <td className={`px-4 py-2 text-right ${s.proficient === 0 ? "text-status-red" : ""}`}>{s.proficient} <span className="text-xs text-brand-grey">({fmtNumber(s.avg * 100, lng, 0)} %)</span></td>
                    {matrixCompanies.map((c) => {
                      const m = cell(s.skill, c);
                      return <td key={c} className="px-3 py-2 text-center">{m ? <span className={`inline-block min-w-[3rem] rounded px-1.5 py-0.5 text-xs font-semibold ${heat(m.avg)}`} title={`${m.n}`}>{fmtNumber(m.avg * 100, lng, 0)} %</span> : <span className="text-brand-grey-light">·</span>}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
